import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Cell,
  ComposedChart,
  Line,
  Area,
  AreaChart,
} from 'recharts';
import {
  Globe,
  CalendarDays,
  TrendingUp,
  Clapperboard,
  BarChart3,
  Scale,
} from 'lucide-react';
import { Select, PlatformIcon, Spinner } from '@/components/primitives';
import type { SelectOption } from '@/components/primitives';
import { SegmentedControl } from '@/components/organisms';
import { ChartLegend } from '@/components/organisms/ChartLegend';
import { ChartTooltip } from '@/components/organisms/ChartTooltip';
import { apiUrl, getJson } from '@/lib/api';
import {
  fetchGenres,
  buildInsights,
  buildPareto,
  buildMomentum,
  GENRE_MEASURE_META,
  type GenreMeasure,
  type GenreInsight,
  type ParetoDatum,
  type MomentumDatum,
} from '@/lib/genres';
import { formatUsd, formatCount, formatShare, shortPlatform, prettyQuarter } from '@/lib/format';
import styles from './TitlesGenres.module.css';

// ─── Series colours (tokens; flip with theme automatically) ──────────────────
// Acquired is the "active growth" bet → brand gold (consistent with Catalog's
// gold = exclusive framing). Retained is the loyal base → indigo. Stacked, gold
// sits at the base so the acquired share reads as a proportion of the whole bar.
const COLOR_ACQUIRED = 'var(--accent-secondary)';
const COLOR_RETAINED = 'var(--data-1)';
const MUTED_OPACITY = 0.28;

// ─── Filter options (platform/region/quarter) ────────────────────────────────
// Reuse the platforms endpoint's filters — same platforms/regions/quarters as the
// rest of the app. (Genres has no filters endpoint of its own; the dimensions match.)
interface Filters {
  platforms: string[];
  regions: string[];
  quarters: string[];
}

function useFilters() {
  return useQuery({
    queryKey: ['platform-filters'],
    queryFn: () => getJson<Filters>(apiUrl('/platforms/filters')),
    staleTime: 10 * 60 * 1000,
  });
}

// ─── Measure formatting ──────────────────────────────────────────────────────
function formatMeasure(value: number, measure: GenreMeasure): string {
  if (value == null) return '—';
  switch (measure) {
    case 'revenue':
      return formatUsd(value);
    case 'subscribers':
      return formatCount(value);
    case 'titles':
      return value.toLocaleString();
  }
}
// ─── v2: Insight banner — 2–3 computed findings across ALL genres ─────────────
const INSIGHT_ICON: Record<GenreInsight['kind'], React.ReactNode> = {
  concentration: <BarChart3 size={16} />,
  uniform: <Scale size={16} />,
  mover: <TrendingUp size={16} />,
};

function InsightBanner({ insights }: { insights: GenreInsight[] }) {
  if (insights.length === 0) return null;
  return (
    <section className={styles.insightBanner} aria-label="Key genre insights">
      {insights.map((ins, i) => (
        <div key={i} className={`${styles.insightCard} ${styles[`insight_${ins.kind}`]}`}>
          <span className={styles.insightIcon} aria-hidden="true">
            {INSIGHT_ICON[ins.kind]}
          </span>
          <p className={styles.insightText}>
            <strong className={styles.insightSubject}>{ins.subject}</strong> {ins.text}
          </p>
        </div>
      ))}
    </section>
  );
}

// ─── v2 Hero: concentration (Pareto) ──────────────────────────────────────────
// Genres ranked by revenue with the acq/ret split inside each bar, plus a cumulative
// share line on a right axis. Answers "how concentrated is genre revenue, and what's
// the mix" — the dominant signal in the data (Drama alone ~45%, top-5 ~77%).

function ParetoTooltip({
  active,
  payload,
  measure,
}: {
  active?: boolean;
  payload?: Array<{ payload: ParetoDatum }>;
  measure: GenreMeasure;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <ChartTooltip
      title={d.label}
      rows={[
        { label: 'Acquired', value: formatMeasure(d.acquired, measure), color: COLOR_ACQUIRED },
        { label: 'Retained', value: formatMeasure(d.retained, measure), color: COLOR_RETAINED },
      ]}
      totals={[
        { label: 'Total', value: formatMeasure(d.total, measure) },
        { label: 'Share of all genres', value: formatShare(d.share) },
        { label: 'Cumulative', value: formatShare(d.cumulative), emphasis: 'primary' },
      ]}
    />
  );
}

function ParetoChart({
  data,
  measure,
  highlight,
  onSelectKey,
}: {
  data: ParetoDatum[];
  measure: GenreMeasure;
  highlight: string;
  onSelectKey: (key: string) => void;
}) {
  const dim = (key: string) => (highlight && key !== highlight ? MUTED_OPACITY : 1);
  return (
    <div className={styles.chartCard}>
      <h3 className={styles.chartTitle}>
        <span className={styles.chartTitleIcon} aria-hidden="true">
          <BarChart3 size={15} />
        </span>
        How concentrated is genre {GENRE_MEASURE_META[measure].short.toLowerCase()}?
      </h3>
      {data.length === 0 ? (
        <p className={styles.empty}>No data for this selection.</p>
      ) : (
        <ResponsiveContainer width="100%" height={420}>
          <ComposedChart data={data} margin={{ top: 12, right: 48, bottom: 4, left: 0 }}>
            <CartesianGrid vertical={false} stroke="var(--border-subtle)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={76}
              tickLine={false}
              axisLine={{ stroke: 'var(--border-subtle)' }}
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
              tickFormatter={(v) => formatMeasure(v, measure)}
              tickLine={false}
              axisLine={false}
              width={56}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, 1]}
              tickFormatter={(v) => formatShare(v)}
              tick={{ fontSize: 11, fill: 'var(--accent-primary)' }}
              tickLine={false}
              axisLine={false}
              width={44}
            />
            <RTooltip cursor={{ fill: 'var(--bg-hover)' }} content={<ParetoTooltip measure={measure} />} />
            <Bar
              yAxisId="left"
              stackId="genre"
              name="Acquired"
              dataKey="acquired"
              fill={COLOR_ACQUIRED}
              onClick={(d: { genre?: string }) => d?.genre && onSelectKey(d.genre)}
              cursor="pointer"
            >
              {data.map((d) => (
                <Cell key={d.genre} fillOpacity={dim(d.genre)} />
              ))}
            </Bar>
            <Bar
              yAxisId="left"
              stackId="genre"
              name="Retained"
              dataKey="retained"
              fill={COLOR_RETAINED}
              radius={[2, 2, 0, 0]}
              onClick={(d: { genre?: string }) => d?.genre && onSelectKey(d.genre)}
              cursor="pointer"
            >
              {data.map((d) => (
                <Cell key={d.genre} fillOpacity={dim(d.genre) * 0.85} />
              ))}
            </Bar>
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="cumulative"
              name="Cumulative share"
              stroke="var(--accent-primary)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      <p className={styles.quadrantNote}>
        Bars = each genre’s {GENRE_MEASURE_META[measure].short.toLowerCase()} (gold acquired · indigo
        retained), ranked high→low · the line is the running cumulative share · click a bar to drill into
        its trend
      </p>
    </div>
  );
}

// ─── v2: Momentum — the selected genre's size over time ───────────────────────
function MomentumTooltip({
  active,
  payload,
  label,
  measure,
}: {
  active?: boolean;
  payload?: Array<{ payload: MomentumDatum }>;
  label?: string;
  measure: GenreMeasure;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <ChartTooltip
      title={label}
      rows={[
        { label: 'Acquired', value: formatMeasure(d.acquired, measure), color: COLOR_ACQUIRED },
        { label: 'Retained', value: formatMeasure(d.retained, measure), color: COLOR_RETAINED },
      ]}
      totals={[{ label: 'Total', value: formatMeasure(d.total, measure) }]}
    />
  );
}

function MomentumChart({
  label,
  data,
  measure,
}: {
  label: string;
  data: MomentumDatum[];
  measure: GenreMeasure;
}) {
  return (
    <div className={styles.chartCard}>
      <h3 className={styles.chartTitle}>
        <span className={styles.chartTitleIcon} aria-hidden="true">
          <Clapperboard size={15} />
        </span>
        {label}: {GENRE_MEASURE_META[measure].short.toLowerCase()} over time
      </h3>
      {data.length === 0 ? (
        <p className={styles.empty}>No data for this selection.</p>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data} margin={{ top: 12, right: 8, bottom: 4, left: 0 }}>
            <defs>
              <linearGradient id="momentumFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--accent-primary)" stopOpacity={0.35} />
                <stop offset="100%" stopColor="var(--accent-primary)" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="var(--border-subtle)" />
            <XAxis
              dataKey="quarter"
              tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={56}
              tickLine={false}
              axisLine={{ stroke: 'var(--border-subtle)' }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
              tickFormatter={(v) => formatMeasure(v, measure)}
              tickLine={false}
              axisLine={false}
              width={56}
            />
            <RTooltip cursor={{ fill: 'var(--bg-hover)' }} content={<MomentumTooltip measure={measure} />} />
            <Area
              type="monotone"
              dataKey="total"
              stroke="var(--accent-primary)"
              strokeWidth={2}
              fill="url(#momentumFill)"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export function TitlesGenres() {
  const { data: filters } = useFilters();

  const [genre, setGenre] = useState('crime'); // micro subject; matches backend default
  const [platform, setPlatform] = useState(''); // '' = all platforms (industry view)
  const [region, setRegion] = useState(''); // '' = all regions
  const [endDate, setEndDate] = useState(''); // '' = latest
  const [measure, setMeasure] = useState<GenreMeasure>('revenue');

  const { data, isLoading, error } = useQuery({
    queryKey: ['genres', genre, platform, region, endDate],
    queryFn: () => fetchGenres({ genre, platform, region, endDate, lastQuarters: 12 }),
    staleTime: 5 * 60 * 1000,
  });

  const platformOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: 'All Platforms' },
      ...(filters?.platforms ?? []).map((p) => ({
        value: p,
        label: shortPlatform(p),
        icon: <PlatformIcon name={p} size={18} />,
      })),
    ],
    [filters],
  );

  const regionOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: 'All Regions' },
      ...(filters?.regions ?? []).map((r) => ({ value: r, label: r })),
    ],
    [filters],
  );

  const latestLabel = data?.macro.quarter
    ? `${prettyQuarter(data.macro.quarter)} (latest)`
    : 'Latest Quarter';

  const quarterOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: latestLabel },
      ...(filters?.quarters ?? []).map((q) => ({ value: q, label: prettyQuarter(q) })),
    ],
    [filters, latestLabel],
  );

  // Genre selector options come from the macro response (the authoritative genre list
  // with clean labels), sorted by total revenue as the backend returns them.
  const genreOptions: SelectOption[] = useMemo(
    () =>
      (data?.macro.genres ?? []).map((g) => ({
        value: g.genre,
        label: g.label,
      })),
    [data],
  );

  // v2: computed insights + the concentration (Pareto) hero + momentum series.
  const insights = useMemo(
    () => (data ? buildInsights(data.macro, data.micro, measure) : []),
    [data, measure],
  );
  const pareto = useMemo(
    () => (data ? buildPareto(data.macro.genres, measure) : []),
    [data, measure],
  );
  const momentum = useMemo(
    () => (data ? buildMomentum(data.micro, measure) : []),
    [data, measure],
  );

  const selectedGenre = data?.micro.genre ?? genre;
  const selectedLabel = data?.micro.label ?? genre;
  const quarterLabel = data?.macro.quarter ? prettyQuarter(data.macro.quarter) : 'the latest quarter';
  const measureLabel = GENRE_MEASURE_META[measure].label.toLowerCase();
  const platformLabel = platform ? shortPlatform(platform) : 'all platforms';

  return (
    <div className={styles.page}>
      {/* Controls */}
      <header className={styles.controls}>
        <div className={styles.selects}>
          <Select
            value={genre}
            options={genreOptions}
            onChange={setGenre}
            searchable
            icon={<TrendingUp size={14} />}
            className={styles.select}
          />
          <Select
            value={platform}
            options={platformOptions}
            onChange={setPlatform}
            searchable
            icon={<PlatformIcon name={platform || 'Netflix'} size={18} />}
            onClear={() => setPlatform('')}
            className={styles.select}
          />
          <Select
            value={region}
            options={regionOptions}
            onChange={setRegion}
            icon={<Globe size={14} />}
            onClear={() => setRegion('')}
            className={styles.select}
          />
          <Select
            value={endDate}
            options={quarterOptions}
            onChange={setEndDate}
            icon={<CalendarDays size={14} />}
            onClear={() => setEndDate('')}
            className={styles.select}
          />
        </div>
        <SegmentedControl
          value={measure}
          onChange={(v) => setMeasure(v as GenreMeasure)}
          options={[
            { value: 'revenue', label: 'Revenue' },
            { value: 'subscribers', label: 'Subs Equiv.' },
            { value: 'titles', label: 'Titles' },
          ]}
        />
      </header>

      {/* Shared legend — one for the whole page */}
      <ChartLegend
        items={[
          { label: 'Acquired', color: COLOR_ACQUIRED },
          { label: 'Retained', color: COLOR_RETAINED },
        ]}
        note={<>Gold = new sign-ups · Indigo = kept subscribers · bar height = total {measureLabel}</>}
      />

      {isLoading && (
        <div className={styles.loading}>
          <Spinner />
        </div>
      )}

      {error && <p className={styles.errorMsg}>Couldn’t load genre data. {(error as Error).message}</p>}

      {data && !isLoading && (
        <>
          {/* v2: computed insight banner — concentration, uniform split, momentum */}
          <InsightBanner insights={insights} />

          {/* v2 Hero: concentration (Pareto) — the dominant signal in the data */}
          <section className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>Genre revenue is top-heavy</h2>
              <p className={styles.sectionMeta}>
                Every genre’s {measureLabel} in {quarterLabel} · {platformLabel} · {region || 'all regions'} ·
                ranked high→low with cumulative share ·{' '}
                <strong className={styles.sectionAccent}>{selectedLabel}</strong> highlighted · click a bar
                to drill in
              </p>
            </div>
            <ParetoChart data={pareto} measure={measure} highlight={selectedGenre} onSelectKey={setGenre} />
          </section>

          {/* Micro — the selected genre's momentum over time (the real variance) */}
          <section className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>{selectedLabel}: growing or shrinking?</h2>
              <p className={styles.sectionMeta}>
                {measureLabel} over the last {momentum.length} quarters · {platformLabel} ·{' '}
                {region || 'all regions'}
              </p>
            </div>
            <MomentumChart label={selectedLabel} data={momentum} measure={measure} />
          </section>
        </>
      )}
    </div>
  );
}
